import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/product.dto';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  isInStock: boolean;
  stockQuantity: number;
  dateAdded: string;
}

export interface CartSummary {
  items: CartItem[];
  totalItems: number;
  subTotal: number;
  taxAmount: number;
  shippingAmount: number;
  totalAmount: number;
  hasOutOfStockItems: boolean;
  lastUpdated: string;
}

export interface AddToCartRequest {
  productId: string;
  quantity: number;
  sessionId?: string;
}

export interface UpdateCartItemRequest {
  cartItemId: string;
  quantity: number;
}

export interface RemoveFromCartRequest {
  cartItemId: string;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private apiUrl = `${environment.apiUrl}/api/arkifihubcart`;
  private cartSummary = signal<CartSummary | null>(null);
  private cartCount = signal<number>(0);
  
  public cartSummary$ = computed(() => this.cartSummary());
  public cartCount$ = computed(() => this.cartCount());

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('No authentication token found. User may need to log in again.');
    }
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  /**
   * Add item to cart
   */
  addToCart(request: AddToCartRequest, platformId: string): Observable<ApiResponse<CartItem>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.post<ApiResponse<CartItem>>(`${this.apiUrl}/add`, request, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(() => {
        // Refresh cart after adding item
        this.refreshCart(platformId);
      })
    );
  }

  /**
   * Update cart item quantity
   */
  updateCartItem(request: UpdateCartItemRequest, platformId: string): Observable<ApiResponse<CartItem>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.put<ApiResponse<CartItem>>(`${this.apiUrl}/update`, request, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(() => {
        // Refresh cart after updating item
        this.refreshCart(platformId);
      })
    );
  }

  /**
   * Remove item from cart
   */
  removeFromCart(request: RemoveFromCartRequest, platformId: string): Observable<ApiResponse<boolean>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.delete<ApiResponse<boolean>>(`${this.apiUrl}/remove`, {
      headers: this.getAuthHeaders(),
      params: params,
      body: request
    }).pipe(
      tap(() => {
        // Refresh cart after removing item
        this.refreshCart(platformId);
      })
    );
  }

  /**
   * Clear entire cart
   */
  clearCart(platformId: string): Observable<ApiResponse<boolean>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.delete<ApiResponse<boolean>>(`${this.apiUrl}/clear`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(() => {
        // Clear local cart
        this.cartSummary.set(null);
        this.cartCount.set(0);
      })
    );
  }

  /**
   * Get cart summary
   */
  getCartSummary(platformId: string): Observable<ApiResponse<CartSummary>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<CartSummary>>(`${this.apiUrl}/summary`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.cartSummary.set(response.data);
          this.cartCount.set(response.data.totalItems);
        }
      })
    );
  }

  /**
   * Get cart items
   */
  getCartItems(platformId: string): Observable<ApiResponse<CartItem[]>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<CartItem[]>>(`${this.apiUrl}/items`, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  /**
   * Get cart item count
   */
  getCartItemCount(platformId: string): Observable<ApiResponse<number>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<number>>(`${this.apiUrl}/count`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(response => {
        if (response.success) {
          this.cartCount.set(response.data);
        }
      })
    );
  }

  /**
   * Get cart total value
   */
  getCartTotalValue(platformId: string): Observable<ApiResponse<number>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<number>>(`${this.apiUrl}/total`, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  /**
   * Refresh cart data
   */
  refreshCart(platformId: string): void {
    this.getCartSummary(platformId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.cartSummary.set(response.data);
          this.cartCount.set(response.data.totalItems);
        }
      },
      error: (error) => {
        console.error('Error refreshing cart:', error);
      }
    });
  }

  /**
   * Initialize cart (call this when user logs in or page loads)
   */
  initializeCart(platformId: string): void {
    this.refreshCart(platformId);
  }

  /**
   * Get current cart state
   */
  getCurrentCart(): CartSummary | null {
    return this.cartSummary();
  }

  /**
   * Generate session ID for anonymous users
   */
  generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }
}
